import { toolLinkHref } from "@/lib/tool-link";
import { DISCORD_URL } from "@/lib/landing-constants";

export type UpdateType = "feature" | "improvement" | "fix" | "announcement";

export type WhatsNewIcon =
  | "Columns"
  | "Percent"
  | "Smartphone"
  | "MessageCircle"
  | "EyeOff"
  | "Plus"
  | "Share2"
  | "Layers"
  | "GraduationCap"
  | "Headphones"
  | "SlidersHorizontal"
  | "RefreshCw"
  | "Save"
  | "Link2"
  | "emoji:🐋"
  | "emoji:🎾"
  | "emoji:🏀"
  | "emoji:🎯"
  | "emoji:🏆"
  | "emoji:⛳"
  | "emoji:🎰"
  | "emoji:🐶"
  | "emoji:🎲"
  | "emoji:📱"
  | "emoji:⚾"
  | "emoji:⚽"
  | "emoji:🏈"
  | "emoji:🥊"
  | "emoji:🔔"
  | "BarChart3";

export interface WhatsNewUpdate {
  id: string;
  date: string;
  title: string;
  description: string;
  type: UpdateType;
  icon?: WhatsNewIcon;
  link?: {
    text: string;
    href: string;
  };
  highlight?: "blue" | "green" | "purple";
  audience?: "chat";
}

export const MAX_UPDATES_SHOWN = 8;

export const WHATS_NEW_UPDATES: WhatsNewUpdate[] = [
  {
    id: "2026-10-21-new-version-prompt",
    date: "2026-10-21",
    title: "Your tools stay on the latest version",
    description:
      "When we ship an update, Upside shows a small Refresh prompt. On the live boards it reloads by itself after 30 seconds so prices keep up. It waits while you have a menu open or an unsent message or bet plan, and you can dismiss it.",
    type: "improvement",
    icon: "RefreshCw",
  },
  {
    id: "2026-10-19-ev-sniper-live-beta",
    date: "2026-10-19",
    title: "Live betting is in beta",
    description:
      "Diamond members can now find +EV plays on games already in progress. On EV Sniper, pick Live under Start time. It's still in testing and in-game prices move fast, so confirm the line at your book before you bet.",
    type: "feature",
    icon: "emoji:🎯",
    link: { text: "Open EV Sniper", href: "/ev-sniper" },
  },
  {
    id: "2026-10-08-chat-is-here",
    date: "2026-10-08",
    title: "Chat is here",
    description: "Talk through plays with other members and share them straight from the boards with Share to chat.",
    type: "feature",
    icon: "MessageCircle",
    link: { text: "Open chat", href: "/community" },
    audience: "chat",
  },
  {
    id: "2026-10-05-race-to-place-alerts",
    date: "2026-10-05",
    title: "Race to Place alerts are here!!",
    description:
      "Diamond members can now get a push the moment Betr posts a Race to Place slip that prices +EV. Follow the tutorial to add Upside to your phone.\n\nOne change to be upfront about: Race to Place is now a Diamond tool. We know this stings (Colin's call, point the finger at him), but with how powerful this feature is and how quickly the RTPs fill, we believe it's well worth it.",
    type: "feature",
    icon: "emoji:🔔",
    highlight: "purple",
    link: {
      text: "Turn alerts on",
      href: "/account/notifications",
    },
  },
  {
    id: "2026-09-04-odds-range-filters",
    date: "2026-09-04",
    title: "Filter By Price",
    description:
      "Whale Watch now takes a min and max odds, so you can hide longshots or juice you would never bet. +EV Sniper gets an Odds to Hit window: keep only plays with a fair win chance between, say, 35% and 80%. Both live under Filters.",
    type: "feature",
    icon: "SlidersHorizontal",
    link: {
      text: "Open Whale Watch",
      href: "/whale-watch",
    },
  },
  {
    id: "2026-09-03-sniper-negative-legs",
    date: "2026-09-03",
    title: "Fill Out Your Slip",
    description:
      "A leg that is slightly negative on its own can still belong to a +EV slip. Drop the Min EV floor in +EV Sniper below zero to see fantasy legs down to -3%, then check the slip EV in the drawer.",
    type: "feature",
    icon: "emoji:🎯",
    link: {
      text: "Open +EV Sniper",
      href: "/ev-sniper",
    },
  },
  {
    id: "2026-09-03-slip-implied-odds",
    date: "2026-09-03",
    title: "Odds That Match Your Slip",
    description:
      "Pick a slip type on Fantasy Props and the book column shows that slip's per-leg price. 3 Pick Power on PrizePicks reads -122, not -119. The slip picker now covers Betr, Dabble and Pick 6 too.",
    type: "feature",
    icon: "Percent",
    link: {
      text: "Open Fantasy Props",
      href: "/optimizer/props",
    },
  },
  {
    id: "2026-08-24-rail-redesign",
    date: "2026-08-24",
    title: "More Room For Plays",
    description:
      "The top bar is gone. Search, community links and these updates moved into the sidebar, and the filter row is tighter, so more of every screen shows plays instead of navigation. Expanded rows, filter buttons and the table footer are all quieter now.",
    type: "improvement",
    icon: "Layers",
  },
  {
    id: "2026-08-13-ufc-live",
    date: "2026-08-13",
    title: "UFC Is Live",
    description:
      "Fight night, covered. UFC fighter props are now live across PrizePicks, Underdog, and your sportsbooks — Significant Strikes, Takedowns, Fight Time, Total Rounds, and Knockdowns, every card.",
    type: "feature",
    icon: "emoji:🥊",
    highlight: "green",
    link: {
      text: "Find UFC edges",
      href: "/optimizer",
    },
  },
  {
    id: "2026-08-10-prop-discrepancies-all-sports",
    date: "2026-08-10",
    title: "Prop Discrepancies Now Covers Every Sport",
    description:
      "Every player prop, every sport, every Book you use. Prop Discrepancies now ranks line gaps across all live sports and leagues, and a new sortable Edge column surfaces the biggest gaps in one click.",
    type: "feature",
    icon: "BarChart3",
    highlight: "green",
    link: {
      text: "Compare prop lines",
      href: "/prop-discrepancies",
    },
  },
  {
    id: "2026-08-07-comparison-column-order",
    date: "2026-08-07",
    title: "Comparison Columns, Your Order",
    description:
      "Put your go-to Books first. Open Filters in any Optimizer, hit Reorder, and arrange the comparison columns in the order you shop. Set it once and every Optimizer keeps it, desktop and mobile.",
    type: "feature",
    icon: "Columns",
    highlight: "green",
    link: {
      text: "Reorder your columns",
      href: "/optimizer",
    },
  },
  {
    id: "2026-08-05-nfl-live",
    date: "2026-08-05",
    title: "NFL Is Back",
    description:
      "Football season is here. Full NFL coverage across Game Lines, Player Props, Whale Watch, Arbitrage, Low Hold, and Odds Screen. 30+ books, preseason through the Super Bowl.",
    type: "feature",
    icon: "emoji:🏈",
    highlight: "green",
    link: {
      text: "Find NFL edges",
      href: "/optimizer",
    },
  },
  {
    id: "2026-07-19-courtside-live",
    date: "2026-07-19",
    title: "Courtside Is Live",
    description:
      "Compare Courtside game lines and player props across MLB, WNBA, tennis, esports, and every supported league throughout Upside.",
    type: "feature",
    icon: "Plus",
    highlight: "green",
    link: {
      text: "Find Courtside edges",
      href: toolLinkHref("/optimizer", { sportsbook: "courtside", platform: "courtside", league: "MLB" }),
    },
  },
  {
    id: "2026-07-10-global-sportsbook-defaults",
    date: "2026-07-10",
    title: "Your Books, Across Every Tool",
    description:
      "Choose the Books you use and one default Book. Multi-Book tools use your list, while single-Book tools remember the last compatible Book you opened.",
    type: "feature",
    icon: "SlidersHorizontal",
    highlight: "green",
    link: {
      text: "Manage Books",
      href: "/account",
    },
  },
  {
    id: "2026-07-09-visual-refresh",
    date: "2026-07-09",
    title: "A Cleaner Look on Every Screen",
    description:
      "We gave the whole app a visual tune-up — sleeker tool headers, quicker filters, and a smoother mobile experience built around the tools you use most. Less clutter between you and your next edge.",
    type: "improvement",
    icon: "Smartphone",
    highlight: "blue",
  },
  {
    id: "2026-06-25-refreshed-odds-screen",
    date: "2026-06-25",
    title: "Refreshed Odds Screen",
    description:
      "Odds Screen now has a wider, cleaner board with data-backed sport, league, market, sportsbook, and line controls. Player props are back too, with alternate lines grouped into easier dropdowns.",
    type: "improvement",
    icon: "BarChart3",
    highlight: "blue",
    link: {
      text: "Open Odds Screen",
      href: "/odds-screen",
    },
  },
  {
    id: "2026-06-11-world-cup-live",
    date: "2026-06-11",
    title: "The World Cup Is Here",
    description:
      "The biggest tournament on the planet, fully covered. Every match across Game Lines, Player Props, Whale Watch, Arbitrage, Low Hold, and Odds Screen. 30+ books, group stage through the final.",
    type: "feature",
    icon: "emoji:⚽",
    highlight: "green",
    link: {
      text: "Find World Cup edges",
      href: "/optimizer",
    },
  },
  {
    id: "2026-05-08-wnba-live",
    date: "2026-05-08",
    title: "WNBA Is Live",
    description:
      "Full WNBA coverage across Game Lines, Player Props, Whale Watch, and Arbitrage. 30+ books, every game, all season.",
    type: "feature",
    icon: "emoji:🏀",
    highlight: "green",
    link: {
      text: "Find WNBA edges",
      href: "/optimizer",
    },
  },
  {
    id: "2026-04-24-hidden-bets-manager",
    date: "2026-04-24",
    title: "Hide Bets Across Tools",
    description:
      "Hide a play once and keep it out of Optimizer, Whale Watch, Low Hold, Arbitrage, and Odds Screen. Open Hidden Bets anytime to review what you hid, restore one play, or bring everything back.",
    type: "feature",
    icon: "EyeOff",
    highlight: "green",
    link: {
      text: "Manage hidden bets",
      href: "/optimizer",
    },
  },
  {
    id: "2026-04-02-filter-ux-redesign",
    date: "2026-04-02",
    title: "Redesigned Filters, Presets, and Sharing",
    description:
      "Mobile filters are now a single tap away with the new Market trigger. Collapsible sections remember which ones you keep open. Save your favorite filter combos as presets and switch strategies instantly. Hit Share to copy a clean link of your exact setup.",
    type: "feature",
    icon: "SlidersHorizontal",
    highlight: "purple",
    link: {
      text: "Try the new filters",
      href: "/optimizer/props",
    },
  },
  {
    id: "2026-04-02-all-books-weights",
    date: "2026-04-02",
    title: "Set Custom Weights for Every Book",
    description:
      "Fair Value Settings now shows every sportsbook available on your optimizer, not just the original consensus list. Set weights for any book your strategy depends on.",
    type: "feature",
    icon: "SlidersHorizontal",
  },
  {
    id: "2026-03-31-odds-screen-upgrades",
    date: "2026-03-31",
    title: "Odds Screen Gets Sharper",
    description:
      "Avg odds, sportsbook sorting, and faster search are now live on Odds Screen. Find the right market faster and see how the best line stacks up at a glance.",
    type: "improvement",
    icon: "BarChart3",
    highlight: "blue",
    link: {
      text: "Open Odds Screen",
      href: "/odds-screen",
    },
  },
  {
    id: "2026-03-31-fair-value-weights-props",
    date: "2026-03-31",
    title: "Custom Fair Value on Props",
    description:
      "Choose which books to trust for fair value, save your presets, and apply them across all three optimizer pages. The same model controls from Game Lines now work on Props.",
    type: "feature",
    icon: "SlidersHorizontal",
    highlight: "green",
    link: {
      text: "Try it on Props",
      href: "/optimizer/props",
    },
  },
  {
    id: "2026-03-31-sticky-filters",
    date: "2026-03-31",
    title: "Filters That Follow You",
    description:
      "Set your book, league, EV threshold, and slip type once. They save to your account across sessions and devices.",
    type: "feature",
    icon: "Save",
    link: {
      text: "Set your filters",
      href: "/optimizer",
    },
  },
  {
    id: "2026-03-31-slip-aware-ev",
    date: "2026-03-31",
    title: "EV% Adjusted for Real Payouts",
    description:
      "EV% now reflects how PrizePicks, Underdog, Pick6, and others actually pay out. Pick your format in the filter bar and only see plays with real edge.",
    type: "feature",
    icon: "Percent",
    highlight: "green",
    link: {
      text: "Find slip edges",
      href: "/optimizer/props",
    },
  },
  {
    id: "2026-03-31-whale-watch-book-logos",
    date: "2026-03-31",
    title: "See Which Books Are +EV",
    description:
      "Whale Watch cards now show logos for every book on the right side of each play. Spot your books at a glance.",
    type: "feature",
    icon: "emoji:🐋",
    link: {
      text: "See it live",
      href: "/whale-watch",
    },
  },
  {
    id: "2026-03-31-live-cell-flash",
    date: "2026-03-31",
    title: "Cells Flash When Odds Move",
    description:
      "Individual cells flash green or amber on live updates. Spot movement instantly without losing your place.",
    type: "improvement",
    icon: "RefreshCw",
  },
  {
    id: "2026-03-25-odds-screen-beta",
    date: "2026-03-25",
    title: "Odds Screen (Beta)",
    description:
      "Compare live odds across 30+ sportsbooks in one view. Moneyline, spreads, and totals for every major sport. Free with email verification. Currently in beta — more features coming soon.",
    type: "feature",
    icon: "BarChart3",
    highlight: "green",
    link: {
      text: "Try the Odds Screen",
      href: "/odds-screen",
    },
  },
  {
    id: "2026-03-24-mlb-live",
    date: "2026-03-24",
    title: "MLB Is Live",
    description:
      "Baseball is here. Full MLB coverage across Game Lines, Player Props, Whale Watch, and Arbitrage. 30+ books, every game, all season.",
    type: "feature",
    icon: "emoji:⚾",
    highlight: "green",
    link: {
      text: "Find MLB edges",
      href: "/optimizer",
    },
  },
  {
    id: "2026-03-16-tailgate-app",
    date: "2026-03-16",
    title: "Tailgate App Now Supported",
    description:
      "Tailgate is live on Game Lines and Whale Watch. Use invite code UPSIDE when downloading to get exclusive access.",
    type: "announcement",
    icon: "emoji:📱",
    highlight: "green",
  },
  {
    id: "2026-03-16-four-new-sportsbooks",
    date: "2026-03-16",
    title: "4 New Sportsbooks Added",
    description:
      "BUSR, STN Sports, and SuperBook are now live across Game Lines, Player Props, and Whale Watch. More books means more edges.",
    type: "feature",
    icon: "Plus",
    link: {
      text: "Start comparing",
      href: "/optimizer",
    },
  },
  {
    id: "2026-03-01-esports-more-books",
    date: "2026-03-01",
    title: "Esports Tool Expanded",
    description:
      "Sleeper is now live in Esports Discrepancies alongside PrizePicks, Underdog, Betr, ParlayPlay, Dabble, and DK Pick6. Multiplier badges now show for every book that offers them.",
    type: "feature",
    icon: "emoji:🎯",
    link: {
      text: "Check esports lines",
      href: "/prop-discrepancies",
    },
  },
  {
    id: "2026-03-01-ten-new-sportsbooks",
    date: "2026-03-01",
    title: "10 New Sportsbooks Added",
    description:
      "Betcris, BetUS, DK Pick6, ParlayPlay, Betr Sportsbook, Fanatics Markets, Rebet Prop City, Shuffle, SportsBetting AG, and Sportzino are now live across Game Lines, Player Props, and Whale Watch.",
    type: "feature",
    icon: "emoji:🎲",
    highlight: "green",
    link: {
      text: "Start comparing",
      href: "/optimizer",
    },
  },
  {
    id: "2026-02-20-underdog-payout-update",
    date: "2026-02-20",
    title: "Underdog Payouts Updated",
    description:
      "Non-insured breakevens are now lower. 2-pick drops to 53.45% (3.5x payout) and 3-pick drops to 53.58% (6.5x payout). More plays qualify.",
    type: "improvement",
    icon: "emoji:🐶",
    link: {
      text: "Find Underdog edges",
      href: toolLinkHref("/optimizer/props", { sportsbook: "underdog" }),
    },
  },
  {
    id: "2026-02-18-bovada-gamelines-whale-watch",
    date: "2026-02-18",
    title: "Bovada on Game Lines + Whale Watch",
    description:
      "Bovada is now available in both Game Lines and Whale Watch. Compare more lines in one place.",
    type: "feature",
    icon: "Plus",
    link: {
      text: "Try it out",
      href: "/optimizer",
    },
  },
  {
    id: "2026-02-11-golf-live",
    date: "2026-02-11",
    title: "Golf Is Live",
    description:
      "PGA Tour, LIV, and major championships are now in the optimizer. Full coverage across 30+ books.",
    type: "feature",
    icon: "emoji:⛳",
    highlight: "green",
    link: {
      text: "Find golf edges",
      href: "/optimizer",
    },
  },
  {
    id: "2026-02-10-ncaaw-darts",
    date: "2026-02-10",
    title: "NCAAW + Darts Now Live",
    description:
      "Women's college basketball and darts are now in the optimizer. Find edges across new markets.",
    type: "feature",
    icon: "emoji:🏀",
    highlight: "green",
    link: {
      text: "Find new edges",
      href: "/optimizer",
    },
  },
  {
    id: "2026-02-10-prizepicks-gamelines",
    date: "2026-02-10",
    title: "PrizePicks on Game Lines",
    description:
      "PrizePicks is now available in Game Lines. Compare their lines against 30+ books.",
    type: "feature",
    icon: "emoji:🏆",
    link: {
      text: "Try it out",
      href: "/optimizer",
    },
  },
  {
    id: "2026-02-09-tennis-live",
    date: "2026-02-09",
    title: "Tennis Is Live",
    description:
      "ATP, WTA, and Grand Slams are now in the optimizer. Full coverage across 30+ books.",
    type: "feature",
    icon: "emoji:🎾",
    highlight: "green",
    link: {
      text: "Find tennis edges",
      href: "/optimizer",
    },
  },
  {
    id: "2026-02-07-filters-drawer",
    date: "2026-02-07",
    title: "Faster Filters",
    description:
      "New side drawer with quick presets. Dial in your filters without leaving the table.",
    type: "improvement",
    icon: "SlidersHorizontal",
    link: {
      text: "Try it out",
      href: "/optimizer",
    },
  },
  {
    id: "2026-02-04-one-click-bets",
    date: "2026-02-04",
    title: "One Click Bets",
    description:
      "See odds, click, bet. Tap any line in Whale Watch, Game Lines, Arbitrage, or Low Hold and go straight to the book's bet slip.",
    type: "feature",
    icon: "Link2",
    link: {
      text: "Try it out",
      href: "/whale-watch",
    },
    highlight: "blue",
  },
  {
    id: "2026-02-03-whale-watch-auto-refresh",
    date: "2026-02-03",
    title: "Whale Watch Live Mode",
    description:
      "Flip on Live for automatic refreshes. Pause it when you want to lock in.",
    type: "feature",
    icon: "RefreshCw",
    link: {
      text: "Try it out",
      href: "/whale-watch",
    },
  },
  {
    id: "2026-02-01-whale-watch-sticky-filters",
    date: "2026-02-01",
    title: "Whale Watch Remembers Your Filters",
    description:
      "Your Whale Watch filters now save to your account. Set your books, edge, and market preferences once and they'll be waiting on any device you log into.",
    type: "feature",
    icon: "Save",
    link: {
      text: "Try it out",
      href: "/whale-watch",
    },
  },
  {
    id: "2026-01-28-whale-watch-enhancements",
    date: "2026-01-28",
    title: "Whale Watch Enhancements 🐋",
    description:
      "More markets, more edge. Whale Watch now covers college basketball and additional market types so you can follow the sharp money across a wider range of action.",
    type: "improvement",
    icon: "emoji:🐋",
    link: {
      text: "Check it out",
      href: "/whale-watch",
    },
  },
  {
    id: "2026-01-26-whale-watch-tutorial",
    date: "2026-01-26",
    title: "Whale Watch Tool 🐋",
    description:
      "Learn how to use Whale Watch to follow the smart money and find high-conviction plays. Watch our quick tutorial to get started.",
    type: "feature",
    icon: "emoji:🐋",
    link: {
      text: "Watch Tutorial",
      href: "https://youtu.be/KrXCHw_7Aew",
    },
  },
  {
    id: "2026-01-16-esports-mobile-redesign",
    date: "2026-01-16",
    title: "Esports Mobile Glow-Up",
    description:
      "Sleeker cards and a new trading spread bar make it easier to spot edges at a glance.",
    type: "improvement",
    icon: "Smartphone",
    link: {
      text: "Check it out",
      href: "/prop-discrepancies",
    },
  },
  {
    id: "2026-01-15-betr-esports",
    date: "2026-01-15",
    title: "Betr Esports 🎮",
    description:
      "Betr is now available in the Esports Discrepancy tool. Compare lines across even more books to spot edges.",
    type: "feature",
    icon: "Plus",
    link: {
      text: "View Esports",
      href: "/prop-discrepancies",
    },
  },
  {
    id: "2026-01-14-odds-format",
    date: "2026-01-14",
    title: "Pick Your Odds Format",
    description:
      "American, Decimal, or Cents—your choice. Find the toggle in Advanced Filters.",
    type: "feature",
    icon: "SlidersHorizontal",
    link: {
      text: "Try it out",
      href: "/optimizer",
    },
  },
  {
    id: "2026-01-06-member-links",
    date: "2026-01-06",
    title: "Share Links. Get Paid.",
    description:
      "Drop your link on socials. Get 50% of every subscription from your referrals.",
    type: "feature",
    icon: "Link2",
    link: {
      text: "Create a link",
      href: "/links",
    },
  },
  {
    id: "2026-01-03-inline-odds-comparison",
    date: "2026-01-03",
    title: "Line Shop in One Tap",
    description:
      "Expand any prop or game line to compare odds across all books—both sides of the bet, one view.",
    type: "feature",
    icon: "Columns",
    link: {
      text: "Try it out",
      href: "/optimizer/props",
    },
  },
  {
    id: "2026-01-02-percent-needed-tooltip",
    date: "2026-01-02",
    title: "See % Needed for Every Slip Type",
    description:
      "Hover (desktop) or tap (mobile) the % To Hit badge to instantly see which slip types each prop qualifies for. No more guessing breakeven thresholds.",
    type: "feature",
    icon: "Percent",
    link: {
      text: "Try it out",
      href: "/optimizer/props",
    },
  },
  {
    id: "2026-01-02-mobile-card-ui",
    date: "2026-01-02",
    title: "Mobile UI Revamp",
    description:
      "Completely redesigned mobile experience with card-based layouts. Swipe through props, sort easily, and switch between card and table views.",
    type: "feature",
    icon: "Smartphone",
    link: {
      text: "Try on mobile",
      href: "/optimizer/props",
    },
  },
  {
    id: "2025-12-30-discord-community",
    date: "2025-12-30",
    title: "Join Our Discord Community",
    description:
      "Connect with other +EV bettors, share picks, and get real-time support. Discord links now available throughout the app.",
    type: "announcement",
    icon: "MessageCircle",
    link: {
      text: "Join Discord",
      href: DISCORD_URL,
    },
  },
  {
    id: "2025-12-28-hide-push-props",
    date: "2025-12-28",
    title: "Hide Push Props Filter",
    description:
      "Filter out props where your line equals the consensus. Focus only on plays with a clear over/under edge.",
    type: "feature",
    icon: "EyeOff",
    link: {
      text: "Try it out",
      href: "/optimizer/props",
    },
  },

  {
    id: "2025-12-24-draftkings-predictions",
    date: "2025-12-24",
    title: "DraftKings Predictions Added",
    description:
      "DraftKings Predictions (DK Predictions) is now available in Game Lines. Another sharp book to compare against.",
    type: "feature",
    icon: "Plus",
    link: {
      text: "Open Optimizer",
      href: "/optimizer",
    },
  },
  {
    id: "2025-12-22-arb-lowhold-books",
    date: "2025-12-22",
    title: "More Books in ARB & Low Hold",
    description:
      "BetRivers, PropBuilder, and MyBookie are now available in Arbitrage and Low Hold tools. Find more opportunities across books.",
    type: "feature",
    icon: "Plus",
    link: {
      text: "View Arbitrage",
      href: "/arbitrage",
    },
  },
  {
    id: "2025-12-17-thescore-gamelines",
    date: "2025-12-17",
    title: "theScore Added to Game Lines",
    description:
      "theScore Bet is now available in Game Lines. Compare odds across even more sportsbooks to maximize your edge.",
    type: "feature",
    icon: "Plus",
    link: {
      text: "Open Optimizer",
      href: "/optimizer",
    },
  },
  {
    id: "2025-12-17-mybookie-fanatics-gamelines",
    date: "2025-12-17",
    title: "MyBookie & Fanatics Added to Game Lines",
    description:
      "Two more sportsbooks now available in Game Lines. Compare odds across even more books to find the best edges.",
    type: "feature",
    icon: "Plus",
    link: {
      text: "Open Optimizer",
      href: "/optimizer",
    },
  },
  {
    id: "2025-12-17-viral-slip-generator",
    date: "2025-12-17",
    title: "Share Your Winning Bet Slips",
    description:
      "Create shareable bet slip images to show off your +EV picks. Perfect for social media and Discord.",
    type: "feature",
    icon: "Share2",
  },
  {
    id: "2025-12-15-league-filter-improvement",
    date: "2025-12-15",
    title: "All Leagues Available From the Start",
    description:
      "League filters now show all available options immediately, making it faster to find the markets you want.",
    type: "improvement",
    icon: "Layers",
  },
  {
    id: "2025-12-10-onboarding-tour",
    date: "2025-12-10",
    title: "Guided Tour for New Users",
    description:
      "New users can now take an interactive tour to learn the platform quickly. Access it anytime from the Help menu.",
    type: "feature",
    icon: "GraduationCap",
    link: {
      text: "Take the tour",
      href: "/optimizer",
    },
  },
  {
    id: "2025-12-08-help-menu",
    date: "2025-12-08",
    title: "Help Menu & Live Chat Support",
    description:
      "Get instant help with the new Help menu. Chat directly with our support team or restart the product tour anytime.",
    type: "feature",
    icon: "Headphones",
  },
  {
    id: "2025-12-06-advanced-filters",
    date: "2025-12-06",
    title: "Advanced Filters for Power Users",
    description:
      "Fine-tune your edge hunting with advanced filters: odds ranges, minimum books, required sportsbooks, and more.",
    type: "feature",
    icon: "SlidersHorizontal",
    link: {
      text: "Try filters",
      href: "/optimizer",
    },
  },
  {
    id: "2025-12-05-auto-refresh-toggle",
    date: "2025-12-05",
    title: "Pause & Resume Live Updates",
    description:
      "Take your time analyzing plays. Pause auto-refresh when you need to focus, resume when you're ready for fresh odds.",
    type: "feature",
    icon: "RefreshCw",
  },
  {
    id: "2025-12-04-weight-presets",
    date: "2025-12-04",
    title: "Save Your Custom Weight Presets",
    description:
      "Create and save your own sportsbook weight configurations. Switch between presets instantly for different strategies.",
    type: "feature",
    icon: "Save",
  },
  {
    id: "2025-11-28-fanduel-gamelines",
    date: "2025-11-28",
    title: "FanDuel Added to Game Lines",
    description:
      "FanDuel is now included in our game lines comparison. Find edges on one of the most popular sportsbooks.",
    type: "feature",
    icon: "Plus",
  },
];

export function visibleUpdates(updates: readonly WhatsNewUpdate[], chat: boolean): readonly WhatsNewUpdate[] {
  return chat ? updates : updates.filter((update) => update.audience !== "chat");
}

export function hasUnreadUpdate(updates: readonly WhatsNewUpdate[], visible: readonly WhatsNewUpdate[], lastSeenId: string | null): boolean {
  if (visible.length === 0) return false;
  const seen = updates.findIndex((update) => update.id === lastSeenId);
  return seen < 0 || updates.indexOf(visible[0]) < seen;
}

export function formatUpdateDate(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}
